<script setup>
import { ref, computed } from 'vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Modal from 'dashboard/components/Modal.vue';

defineProps({
  show: {
    type: Boolean,
    default: false,
  },
  conversation: {
    type: Object,
    required: true,
  },
  message: {
    type: String,
    default: '',
  },
});

const emit = defineEmits(['close', 'schedule']);

const scheduledDate = ref('');
const scheduledTime = ref('');

const minDateTime = computed(() => {
  const now = new Date();
  now.setMinutes(now.getMinutes() + 1);
  return now.toISOString().slice(0, 16);
});

const scheduledAt = computed(() => {
  if (!scheduledDate.value || !scheduledTime.value) return null;

  return new Date(`${scheduledDate.value}T${scheduledTime.value}`);
});

const isValid = computed(() => {
  return scheduledAt.value && scheduledAt.value > new Date();
});

const resetForm = () => {
  scheduledDate.value = '';
  scheduledTime.value = '';
};

const handleClose = () => {
  emit('close');
  resetForm();
};

const handleConfirm = () => {
  if (!isValid.value) return;

  emit('schedule', {
    scheduledAt: scheduledAt.value.toISOString(),
  });

  handleClose();
};
</script>

<template>
  <Modal :show="show" :on-close="handleClose" close-on-backdrop-click>
    <div
      class="flex w-full flex-col overflow-hidden rounded-xl bg-[#23252D] shadow-xl"
    >
      <div class="flex items-center gap-3 border-b border-n-weak/40 px-6 py-5">
        <div
          class="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-n-alpha-2 text-[#90939A]"
        >
          <span class="i-lucide-calendar-clock h-5 w-5" />
        </div>
        <div class="min-w-0">
          <h3 class="text-base font-semibold leading-6 text-n-text">
            {{ $t('CONVERSATION.SCHEDULED_MESSAGE.TITLE') }}
          </h3>
          <p class="mt-0.5 text-sm text-n-text-muted">
            {{ $t('CONVERSATION.SCHEDULED_MESSAGE.DESCRIPTION') }}
          </p>
        </div>
      </div>

      <div class="space-y-5 px-6 py-5">
        <div
          class="flex items-center justify-between gap-3 rounded-lg bg-n-alpha-black2 px-3 py-2.5 text-sm outline outline-1 outline-n-weak/60"
        >
          <span class="font-medium text-n-text-muted">
            {{ $t('CONVERSATION.SCHEDULED_MESSAGE.INBOX_LABEL') }}
          </span>
          <span class="min-w-0 truncate font-medium text-n-text">
            {{
              conversation.inbox_name ||
              $t('CONVERSATION.SCHEDULED_MESSAGE.CURRENT_INBOX')
            }}
          </span>
        </div>

        <div>
          <label class="mb-2 block text-sm font-medium text-n-text">
            {{ $t('CONVERSATION.SCHEDULED_MESSAGE.CONTENT_LABEL') }}
          </label>
          <div
            class="max-h-36 min-h-24 w-full overflow-y-auto rounded-lg bg-n-alpha-black2 px-3 py-2.5 text-sm leading-5 text-n-text outline outline-1 outline-n-weak/60"
          >
            {{
              message ||
              $t('CONVERSATION.SCHEDULED_MESSAGE.CONTENT_PLACEHOLDER')
            }}
          </div>
        </div>

        <div class="space-y-3">
          <label class="block text-sm font-medium text-n-text">
            {{ $t('CONVERSATION.SCHEDULED_MESSAGE.SCHEDULE_LABEL') }}
          </label>
          <div class="grid gap-3 sm:grid-cols-2">
            <label class="space-y-1.5">
              <span class="text-xs font-medium text-n-text-muted">
                {{ $t('CONVERSATION.SCHEDULED_MESSAGE.DATE_LABEL') }}
              </span>
              <input
                v-model="scheduledDate"
                type="date"
                :min="minDateTime.split('T')[0]"
                class="h-10 w-full rounded-lg bg-n-alpha-black2 px-3 text-sm text-n-text outline outline-1 outline-n-weak/60 transition-colors hover:outline-n-slate-6 focus:outline-n-brand"
              />
            </label>
            <label class="space-y-1.5">
              <span class="text-xs font-medium text-n-text-muted">
                {{ $t('CONVERSATION.SCHEDULED_MESSAGE.TIME_LABEL') }}
              </span>
              <input
                v-model="scheduledTime"
                type="time"
                class="h-10 w-full rounded-lg bg-n-alpha-black2 px-3 text-sm text-n-text outline outline-1 outline-n-weak/60 transition-colors hover:outline-n-slate-6 focus:outline-n-brand"
              />
            </label>
          </div>
        </div>
      </div>

      <div
        class="flex items-center justify-end gap-2 border-t border-n-weak/40 bg-[#23252D] px-6 py-4"
      >
        <NextButton
          faded
          slate
          :label="$t('CONVERSATION.SCHEDULED_MESSAGE.CANCEL')"
          @click="handleClose"
        />
        <NextButton
          icon="i-lucide-calendar-check"
          :label="$t('CONVERSATION.SCHEDULED_MESSAGE.CONFIRM')"
          :disabled="!isValid"
          @click="handleConfirm"
        />
      </div>
    </div>
  </Modal>
</template>
